import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import _ from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { useSearchParams } from 'react-router-dom';
import { BoolEnum, Event, EventFeature, EventSession, EventTypeEnum, User } from '../../../API';
import { Logo } from '../../../assets/icons';
import { Card } from '../../../components';
import { Button } from '../../../components/button/Button';
import { EventListItem } from '../../../components/event-list-item/EventListItem';
import { FeatureListItem } from '../../../components/feature-list-item/FeatureListItem';
import { Colors } from '../../../constants';
import UserContext from '../../../contexts/UserContext';
import useCheckMembership from '../../../hooks/useCheckMembership';
import useGetUpcomingEvents from '../../../hooks/useGetUpcomingEvents';
import useGetUserEventSessions from '../../../hooks/useGetUserEventSessions';
import { createEventPaymentIntent } from '../../../services/events';
import { LoginForm } from '../../auth/components/LoginForm';
import { SignupForm } from '../../auth/components/SignupForm';

import { addACTag } from '../../../services/activeCampaign';
import { addEventSessionAttendeeToChatChannel, saveEventSessionAttendee } from '../../../services/events';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string);

const CheckoutForm = ({ eventId, eventSessionId, amount }: { eventId: string; eventSessionId: string; amount: number }) => {
  const stripe = useStripe();
  const elements = useElements();
  const alert = useAlert();

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: any) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setLoading(true);

    const result = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/groups/confirm?eid=${eventId}&esid=${eventSessionId}`,
      },
    });

    setLoading(false);

    if (result.error) {
      // Show error to your customer (for example, payment details incomplete)
      alert.error(result.error.message);
    } else {
      alert.success('Payment successful');
      // Your customer will be redirected to your `return_url`. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {!loading && <div className="mt-10">{stripe && <Button text={`Pay $${amount}`} type="submit" primary />}</div>}
      {loading && <div className="mt-10">Submitting payment...</div>}
    </form>
  );
};

function PaymentForm({ clientSecret, eventId, eventSessionId, amount }: { clientSecret: string; eventId: string; eventSessionId: string; amount: number }) {
  return (
    <Elements stripe={stripePromise} options={{ clientSecret }}>
      <CheckoutForm eventId={eventId} eventSessionId={eventSessionId} amount={amount} />
    </Elements>
  );
}

const RSVPForm = (props: { user: User; selectedSession: EventSession; event: Event }) => {
  const { user, selectedSession, event } = props;
  //complete the RSVP when the button is clicked
  const [loading, setLoading] = useState(false);

  const saveAttendee = useCallback(
    async (currentUser: any, eventSession: any, rawPaymentLog: string | null = null) => {
      await saveEventSessionAttendee(
        {
          userId: currentUser?.id,
          eventSessionId: eventSession?.id,
          eventId: event?.id,
          confirmed: BoolEnum.FALSE,
          metadata: {
            paymentTimestamp: Math.round(Date.now() / 1000),
            rawPaymentLog,
          },
        },
        { forceCreate: true }
      );
    },
    [event]
  );

  const handleCompleteRSVP = useCallback(async () => {
    setLoading(true);
    await saveAttendee(user, selectedSession, '{}');
    //add the user to the event session chat channel
    await addEventSessionAttendeeToChatChannel(user?.id, selectedSession);
    //save the active campaign tags
    const { firstName, lastName, email, phone } = user;
    await addACTag({
      user: { firstName, lastName, email, phone },
      tags: `${selectedSession?.metadata?.activeCampaignTagId}`,
    });
    setLoading(false);
    window.location.replace('https://www.weflourish.com/sgp-confirmation');
  }, [saveAttendee, user, selectedSession]);

  return (
    <Card classes="w-full">
      <h1 className="mb-2 text-[1.5rem] leading-tight font-analogue">Free for Sprout and Bloom members</h1>
      <h2 className="mb-5">RSVP below so that we know that you will be there!</h2>
      <Button text={`RSVP`} type="submit" primary disabled={loading} onClick={handleCompleteRSVP} />
    </Card>
  );
};

function EventTitleHeader(props: any) {
  return (
    <div className="mt-10">
      {!props.hasActivePaidPlan && <h2 className="text-4xl text-[#52663F] font-bold">${_.get(props.event, ['price'], 0.0)}</h2>}
      {props.hasActivePaidPlan && <h2 className="text-2xl text-[#52663F] font-bold">Included with membership</h2>}
      <h1 className="text-[2.5rem] leading-tight font-analogue" data-testid="eventTitleText">
        {_.get(props.event, ['name'], 'Connection Club')}
      </h1>
      <span className="leading-loose font-thin" data-testid="eventSubtitleText">
        {_.get(props.event, ['title'], 0.0)}
      </span>
    </div>
  );
}

const GroupRegistration = () => {
  const [selectedTime, setSelectedTime] = useState<any>(null);
  const [showTimeSelect, setShowTimeSelect] = useState(true);
  const [showSignupForm, setShowSignupForm] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [phoneContinue, setphoneContinue] = useState(false);
  const [intent, setIntent] = useState(null);
  const [signupComplete, setSignupComplete] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [event, setEvent] = useState<Event | null>(null);
  const [searchParams] = useSearchParams();
  const { hasActivePaidPlan } = useCheckMembership(user?.id as string);

  const { currentUser } = useContext(UserContext);
  const { data } = useGetUpcomingEvents(EventTypeEnum.SMALL_GROUP, 1, searchParams.get('eid'));

  //get the user's event sessions for this event
  const { data: userEventSessions } = useGetUserEventSessions((user as User)?.id, _.get(event, ['id']));

  const handleConfirmTime = useCallback(() => {
    if (selectedTime !== null) {
      setShowTimeSelect(false);
      setShowSignupForm(true);
      setphoneContinue(true);
    }
  }, [selectedTime]);

  const handleSignupSuccess = useCallback(
    async (user: User) => {
      if (!data || !selectedTime?.id || !event) return;

      setSignupComplete(true);
      setUser(user as User);
      if (!hasActivePaidPlan) {
        const intentReq = await createEventPaymentIntent((user as User)?.id, _.get(event, ['metadata', 'stripePriceId'], ''), {
          eventId: _.get(event, ['id']),
          eventSessionId: selectedTime?.id,
          userId: (user as User)?.id,
        });
        //now get the payment intent for the payment
        setIntent(intentReq);
      }
    },
    [data, selectedTime, event, hasActivePaidPlan]
  );

  useEffect(() => {
    if (currentUser && currentUser?.username !== null) {
      setSignupComplete(true);
      setUser(_.get(currentUser, ['user']));
      handleSignupSuccess(_.get(currentUser, ['user']));
    }
  }, [currentUser, handleSignupSuccess]);

  useEffect(() => {
    if (!userEventSessions) return;
    if (_.isArray(userEventSessions) && userEventSessions.length > 0) {
      //redirect because it seems the user is already registered
      window.location.replace('https://www.weflourish.com/sgp-confirmation');
    }
  }, [userEventSessions]);

  useEffect(() => {
    if (!data || data === undefined) return;
    setEvent(_.get(data, [0], data) ?? {});
  }, [data]);

  return (
    <div className="grid md:grid-cols-2">
      <div className="h-screen flex justify-center bg-stone drop-shadow-2xl xs:p-5 sm:p-10 lg:p-20 xs:hidden md:block">
        <div className="flex items-start justify-start flex-col">
          <div className="flex items-start justify-start w-full mb-10">
            <Logo width="120" fill={Colors.ginger} />
          </div>
          <EventTitleHeader event={event} hasActivePaidPlan={hasActivePaidPlan} />
          {_.has(event, ['features']) && (
            <div className="mt-10">
              <div className="font-bold mb-2">Your purchase includes:</div>

              {_.isArray(event?.features) &&
                event?.features.map((item: EventFeature, k: number) => (
                  <FeatureListItem index={0} title={item.title} description={item.description as string} key={k} />
                ))}
            </div>
          )}
        </div>
      </div>
      <div className="md:h-screen bg-gradient-to-br from-periwinkle-200 via-meringue100 to-blush-100 flex flex-col items-center justify-center xs:p-5 md:p-10">
        <div className="mb-10 md:hidden xs:block">
          <Logo width="140" fill={Colors.ginger} />
        </div>
        {!phoneContinue && (
          <Card classes="md:hidden xs:block">
            <EventTitleHeader event={event} hasActivePaidPlan={hasActivePaidPlan} />
            {_.has(event, ['features']) && (
              <div className="mt-10">
                <div className="font-bold mb-2">Your purchase includes:</div>

                {_.isArray(event?.features) &&
                  event?.features.map((item: EventFeature, k: number) => (
                    <FeatureListItem index={0} title={item.title} description={item.description as string} key={k} />
                  ))}
              </div>
            )}
            <div className="mt-10">
              <Button text="Continue" onClick={() => setphoneContinue(true)} primary classes="font-semibold" testID="mobileContinueButton" />
            </div>
          </Card>
        )}
        {/* Choose group time */}
        {showTimeSelect && (
          <Card classes={`w-full ${phoneContinue ? 'xs:block' : 'xs:hidden'} md:block`}>
            <div className="flex flex-col mb-4 text-center">
              <h1 className="text-lg xs:text-3xl font-analogue">
                Select an available time <br />
              </h1>
              <span className="leading-loose font-thin">We’ll wait while you check your calendar. </span>
            </div>
            {_.has(event, ['sessions', 'items']) && (
              <div className="mt-8">
                {_.get(event, ['sessions', 'items'], []).map((item: EventSession, k: number) => (
                  <EventListItem
                    startDate={item?.startDate}
                    spotsLeft={item?.attendeeLimit - (item?.eventSessionAttendees?.items?.length ?? 0)}
                    onSelect={() => setSelectedTime(item)}
                    active={selectedTime?.id === item.id}
                    key={k}
                    index={k}
                  />
                ))}
              </div>
            )}
            <div className="mt-10">
              <Button
                text="Confirm discussion time"
                onClick={handleConfirmTime}
                primary
                classes="font-semibold"
                disabled={selectedTime === null}
                testID="confirmDiscussionTimeButton"
              />
            </div>
          </Card>
        )}

        {/* Signup */}
        {showSignupForm && !signupComplete && !user && (
          <Card classes="w-full">
            <div className="flex flex-col mb-6 text-center">
              <h1 className="text-lg xs:text-3xl font-analogue" data-testid="signupHeaderText">
                Create an account
              </h1>
            </div>
            <SignupForm
              onSignupSuccess={async (user: unknown) => {
                handleSignupSuccess(user as User);
              }}
            />
            <div className="flex flex-col mt-6 text-center">
              <Button
                text="Already a member? Log in"
                data-testid="loginButton"
                onClick={() => {
                  setShowSignupForm(false);
                  setShowLoginForm(true);
                }}
              />
            </div>
          </Card>
        )}

        {/* Login if user so chooses */}
        {showLoginForm && !user && (
          <Card classes="w-full">
            <div className="flex flex-col mb-6 text-center">
              <h1 className="text-lg xs:text-3xl font-analogue" data-testid="">
                Login
              </h1>
            </div>
            <LoginForm
              onLoginSuccess={async (user: unknown) => {
                handleSignupSuccess(user as User);
              }}
            />
            <div className="flex flex-col mt-6 text-center">
              <Button
                text="New to Flourish? Sign up"
                data-testid="signupButton"
                onClick={() => {
                  setShowSignupForm(true);
                  setShowLoginForm(false);
                }}
              />
            </div>
          </Card>
        )}

        {/* Pay */}
        {signupComplete && !showTimeSelect && (
          <>
            {!hasActivePaidPlan && intent !== null && (
              <Card classes="w-full">
                <PaymentForm
                  clientSecret={(intent as any)?.paymentIntent as string}
                  eventId={_.get(event, ['id'], '')}
                  eventSessionId={selectedTime?.id}
                  amount={_.get(event, ['price'], 0.0)}
                />
              </Card>
            )}
            {intent === null && <div className="flex items-start justify-start">Loading...</div>}
            {hasActivePaidPlan && <RSVPForm user={user as User} selectedSession={selectedTime} event={event as Event} />}
          </>
        )}
      </div>
    </div>
  );
};

export default GroupRegistration;
